import React, { useEffect, useState } from 'react';
import { css, Theme } from '@emotion/react';
import { BirdiTheme } from '@birdi/theme';
import { stroot, organisation as organisationAPI } from '@birdi/js-sdk';
import { useMetaSelector, updateOrganisation } from '../../meta/reducer';
import { useDispatch } from 'react-redux';
import { buttonBlue, forms, blocks } from '@birdi/theme';
import Select from 'react-select';
import { forms as themeForms } from '@birdi/theme';
import * as Toast from '@radix-ui/react-toast';
import * as Dialog from '@radix-ui/react-dialog';
import {
  closeButtonCSS,
  contentCSS,
  portalCSS,
  paddedButtonFooterCSS,
} from '@birdi/modal-kit';
import CloseSVG from '@birdi/icons/close.svg';
import * as styles from '../styles';
import HandsSVG from '@birdi/icons/thank-you.svg';
import { Organisations } from '@birdi/types';

const dataRegionOptions = Organisations.DATA_REGION_OPTIONS;

interface Fields {
  name: string;
  url: string;
}

export const General = (): React.ReactElement => {
  const dispatch = useDispatch();
  const meta = useMetaSelector();
  const [orgName, setOrgName] = useState('');
  const [orgUrl, setOrgUrl] = useState('');
  const [toastOpen, setToastOpen] = React.useState(false);
  const [regionModalOpen, setRegionModalOpen] = useState(false);
  const [targetRegion, setTargetRegion] = useState<string | null>(null);
  const [regionRequestSent, setRegionRequestSent] = useState(false);
  const [regionRequestError, setRegionRequestError] = useState('');
  const [regionSubmitting, setRegionSubmitting] = useState(false);
  const isWorkspaceContext = !!meta.workspace;
  const isOrgAdmin = meta.permissions?.includes('organisation:edit');
  const currentRegion = meta.organisation?.s3Region || 'ap-southeast-2';
  const currentRegionLabel =
    dataRegionOptions.find((o) => o.value === currentRegion)?.label ||
    'Sydney, Australia';

  useEffect(() => {
    setOrgName(
      isWorkspaceContext
        ? meta.workspace?.name || ''
        : meta.organisation.name || '',
    );
  }, []);

  const updateFields = async (fieldsToUpdate: Fields) => {
    await organisationAPI.updateBranding(
      stroot('iy3ohb'),
      isWorkspaceContext
        ? meta.workspace?.id || ''
        : meta.organisation.id || '',
      fieldsToUpdate,
    );
    dispatch(updateOrganisation(fieldsToUpdate));
  };

  return (
    <div>
      <div css={blocks.pageTitleHolder}>
        <h2 css={blocks.pageTitle}>General</h2>
      </div>
      <div css={blocks.pageGraySection}>
        <p>Manage your organization's information.</p>
      </div>
      <div
        css={css`
          padding: 1em;
          max-width: 35em;
        `}
      >
        <p>
          {isWorkspaceContext
            ? 'Workspace name'
            : 'Organization name (billing name)'}
        </p>
        <div
          css={css`
            display: flex;
            flex-direction: row;
          `}
        >
          <input
            css={(theme) => css`
              ${forms.textInput(theme)};
              margin-right: 1em;
              max-width: 25em;
            `}
            type="text"
            id="orgName"
            defaultValue={
              orgName || meta.workspace?.name || meta.organisation.name
            }
            onChange={(event) => setOrgName(event.target.value)}
          />
        </div>
        {/* <p css={(theme) => css`color: ${theme.flora}; font-weight: bold`}>Coming Soon ...</p>
        <div>
          <p css={(theme) => css`color: ${theme.mono40}; margin-top: 0`}>Organisation URL (Web address)</p>
          <div css={css`display: flex; flex-direction: column`}>
            <div css={css`display: flex; flex-direction: row;`}>
              <input
                disabled
                css={(theme) => css`
                  ${textInput(theme)};
                  margin-right: 0.25em;
                  margin-bottom: 1em;
                  max-width: 25em;
                `}
                type="text"
                id="orgUrl"
                // defaultValue={orgUrl || meta.organisation.url}
                // onChange={(event) => setOrgUrl(event.target.value)}
              />
              <p css={(theme) => css`color: ${theme.mono40}`}>.birdi.io</p>
            </div>
          </div>
        </div> */}
        <Toast.Provider>
          <button
            type="button"
            css={(theme) => css`
              ${buttonBlue(theme)};
              height: 2.5em;
              margin-top: 1em;
            `}
            onClick={() => {
              updateFields({ name: orgName });
              setToastOpen(true);
              window.setTimeout(() => setToastOpen(false), 2000);
            }}
            disabled={!orgName || meta.organisation.name === orgName}
          >
            Save changes
          </button>
          <Toast.Root css={styles.toastRoot} open={toastOpen}>
            <Toast.Description css={styles.toastContent}>
              <HandsSVG />
              Organisation name updated
            </Toast.Description>
          </Toast.Root>
          <Toast.Viewport css={styles.toastViewport} />
        </Toast.Provider>
        {!isWorkspaceContext && (
          <>
            <p
              css={css`
                margin-top: 2em;
                margin-bottom: 0.25em;
              `}
            >
              Data region
            </p>
            <p
              css={(theme: BirdiTheme) => css`
                font-size: 0.9375em;
                color: ${theme.mono70};
                margin: 0 0 0.75em 0;
              `}
            >
              {currentRegionLabel}
            </p>
            {isOrgAdmin && !regionRequestSent && (
              <button
                type="button"
                css={(theme) => css`
                  ${buttonBlue(theme)};
                  height: 2.5em;
                `}
                onClick={() => {
                  setRegionModalOpen(true);
                  setTargetRegion(null);
                  setRegionRequestError('');
                }}
              >
                Request region change
              </button>
            )}
            {regionRequestSent && (
              <p
                css={(theme: BirdiTheme) => css`
                  color: ${theme.flora};
                  font-size: 0.875em;
                  font-weight: 500;
                `}
              >
                Request submitted. Our team will be in touch.
              </p>
            )}
          </>
        )}
        <Dialog.Root open={regionModalOpen} onOpenChange={setRegionModalOpen}>
          <Dialog.Portal>
            <Dialog.Overlay />
            <div css={portalCSS}>
              <Dialog.Content css={contentCSS}>
                <Dialog.Close css={closeButtonCSS}>
                  <CloseSVG />
                </Dialog.Close>
                <h2
                  css={css`
                    margin-bottom: 0.5em;
                  `}
                >
                  Request region change
                </h2>
                <p
                  css={(theme: BirdiTheme) => css`
                    color: ${theme.mono60};
                    font-size: 0.875em;
                    line-height: 1.5;
                    margin: 0 0 1.5em 0;
                  `}
                >
                  Region changes require a data migration and will be reviewed
                  by our team.
                </p>
                <p
                  css={(theme: BirdiTheme) => css`
                    font-size: 0.875em;
                    color: ${theme.mono70};
                    margin: 0 0 0.25em 0;
                    font-weight: 500;
                  `}
                >
                  Current region
                </p>
                <p
                  css={(theme: BirdiTheme) => css`
                    font-size: 0.9375em;
                    color: ${theme.mono60};
                    margin: 0 0 1.25em 0;
                  `}
                >
                  {currentRegionLabel}
                </p>
                <p
                  css={(theme: BirdiTheme) => css`
                    font-size: 0.875em;
                    color: ${theme.mono70};
                    margin: 0 0 0.25em 0;
                    font-weight: 500;
                  `}
                >
                  New region
                </p>
                <Select
                  options={dataRegionOptions.filter(
                    (o) => o.value !== currentRegion,
                  )}
                  placeholder="Select a new region"
                  onChange={(option: any) =>
                    setTargetRegion(option?.value || null)
                  }
                  styles={themeForms.reactSelect}
                />
                {regionRequestError && (
                  <p
                    css={css`
                      color: #ef4444;
                      font-size: 0.875em;
                      margin-top: 0.5em;
                    `}
                  >
                    {regionRequestError}
                  </p>
                )}
                <div css={paddedButtonFooterCSS}>
                  <Dialog.Close asChild>
                    <button
                      type="button"
                      css={(theme: BirdiTheme) => css`
                        background: none;
                        border: 1px solid ${theme.mono30};
                        border-radius: 0.375rem;
                        padding: 0.5em 1.25em;
                        font-size: 0.875em;
                        color: ${theme.mono70};
                        cursor: pointer;
                        &:hover {
                          border-color: ${theme.mono40};
                          background-color: ${theme.mono10};
                        }
                      `}
                    >
                      Cancel
                    </button>
                  </Dialog.Close>
                  <button
                    type="button"
                    css={(theme) => css`
                      ${buttonBlue(theme)};
                      padding: 0.5em 1.25em;
                    `}
                    disabled={!targetRegion || regionSubmitting}
                    onClick={async () => {
                      setRegionSubmitting(true);
                      try {
                        await organisationAPI.requestRegionChange(
                          stroot('reg2ch'),
                          {
                            targetRegion: targetRegion!,
                          },
                        );
                        setRegionRequestSent(true);
                        setRegionRequestError('');
                        setRegionModalOpen(false);
                      } catch (err) {
                        setRegionRequestError(
                          'Failed to submit request. Please try again.',
                        );
                      } finally {
                        setRegionSubmitting(false);
                      }
                    }}
                  >
                    {regionSubmitting
                      ? 'Submitting...'
                      : 'Request region change'}
                  </button>
                </div>
              </Dialog.Content>
            </div>
          </Dialog.Portal>
        </Dialog.Root>
      </div>
    </div>
  );
};
